// Director's Daily Brief (Feature B) — the governed AI brief that opens the
// Executive Command Center pulse view. It loads the latest governed brief for
// the signed-in director on mount, renders the four-paragraph narrative with
// full governed-run chrome (run id, confidence, evidence count, guardrail),
// live-posture + corpus citations, and a "Pending human review" chip. "Refresh
// brief" runs a fresh governed generation grounded on live posture; Acknowledge
// records human review; Dismiss hides the card for the session.
//
// Anti-invention: the numbers come from the server-assembled fact block, never
// from the client. The card only displays what the governed run returned.
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import {
  StatusChip,
  ErrorState,
  AiGroundingChip,
  activityRail,
  toast,
} from '../../components/index.js';
import { GovernedRunChrome } from '../../components/inspectors/GovernedRunChrome.jsx';
import {
  ProvenanceBadge,
  normalizedProvenance,
} from '../../components/ProvenanceBadge.jsx';
import './directorDailyBrief.css';

const DISMISS_KEY = 'dha_prime__daily_brief_dismissed';
const BUSY_LABEL = 'Preparing brief with execution provenance…';
const ACKNOWLEDGED_STATES = new Set(['human_reviewed', 'approve_for_export']);

// The model is instructed to write plain prose, but a live LLM can still slip
// markdown tokens or duplicate the chrome ("Pending human review.", a trailing
// "Citations: [n]" list). The card owns that chrome, so strip the noise here.
function sanitizeNarrative(text = '') {
  return String(text)
    .replace(/^#{1,6}\s+.*$/gm, '') // markdown heading/title lines
    .replace(/\*\*([^*]+)\*\*/g, '$1') // bold markers
    .replace(/^\s*[-•]\s+/gm, '') // stray bullet markers
    .replace(/^\s*citations?:?\s*(\[\d+\][,\s]*)+\.?\s*$/gim, '') // citations trailer
    .replace(/^\s*pending human review\.?\s*$/gim, ''); // chrome already shows this
}

function splitParagraphs(narrative = '') {
  return sanitizeNarrative(narrative)
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

export const DirectorDailyBriefCard = forwardRef(function DirectorDailyBriefCard(
  { scopeId = '' },
  ref,
) {
  const query = scopeId ? { scopeId } : undefined;
  const latest = useApi('/ai/daily-brief/latest', { query });
  const refreshMut = useMutation();
  const reviewMut = useMutation();
  const containerRef = useRef(null);
  const [brief, setBrief] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Sync the displayed brief from the latest fetch — but only when a real run
  // loads. An empty `{}` (no brief yet) leaves the empty state in place.
  useEffect(() => {
    const loaded = latest.data?.data;
    if (loaded && loaded.runId) {
      setBrief(loaded);
      setAcknowledged(ACKNOWLEDGED_STATES.has(String(loaded.reviewState || '')));
    }
  }, [latest.data]);

  async function refreshBrief() {
    containerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    try {
      const body = await refreshMut.run(() =>
        api.post('/ai/daily-brief', scopeId ? { scopeId } : {}),
      );
      const next = body?.data ?? body;
      if (next && next.runId) {
        setBrief(next);
        setAcknowledged(false);
        activityRail.push({
          action: 'ai.generate.daily_brief',
          targetType: 'ai_run',
          targetId: next.runId,
          actorUserId: 'you',
          summary: "Director's daily brief (pending review)",
        });
      }
    } catch {
      // Inline ErrorState renders from refreshMut.error — never blank the card.
    }
  }

  // Exposed so the "Draft AI narrative" action on the command center can surface
  // a fresh governed narrative here instead of dead-ending in a toast.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(ref, () => ({ refresh: refreshBrief }), [scopeId]);

  async function acknowledgeBrief() {
    if (!brief?.runId) return;
    try {
      await reviewMut.run(() =>
        api.post(`/ai/runs/${brief.runId}/review`, { action: 'human_reviewed' }),
      );
      setAcknowledged(true);
      setBrief((prev) =>
        prev
          ? { ...prev, reviewState: 'human_reviewed', humanReviewState: 'human_reviewed' }
          : prev,
      );
      toast.success({
        title: 'Brief acknowledged',
        body: `${brief.runId} recorded as human reviewed.`,
      });
      activityRail.push({
        action: 'ai_run.human_reviewed',
        targetType: 'ai_run',
        targetId: brief.runId,
        actorUserId: 'you',
      });
    } catch (e) {
      toast.error({ title: 'Could not acknowledge', body: e.message });
    }
  }

  function dismissBrief() {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore storage failures */
    }
    setDismissed(true);
  }

  if (dismissed) return null;

  const hasBrief = Boolean(brief?.runId);
  const provenance = hasBrief ? normalizedProvenance(brief, 'ai') : null;
  const initialLoading = latest.loading && !hasBrief;
  // Server-built paragraphs get the same sanitation as raw narrative — the live
  // model can emit chrome (citation trailers, sign-offs) through either path.
  const rawParagraphs = brief?.paragraphs?.length
    ? brief.paragraphs
    : splitParagraphs(brief?.narrative);
  const paragraphs = rawParagraphs
    .map((paragraph) => sanitizeNarrative(paragraph).trim())
    .filter((paragraph) => paragraph && !/^citations?:?[\s[\]0-9,.]*$/i.test(paragraph));
  const generatedAt = brief?.generatedAt ? new Date(brief.generatedAt) : null;
  const asOfLabel =
    generatedAt && !Number.isNaN(generatedAt.getTime())
      ? `As of ${generatedAt.toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}`
      : null;

  return (
    <section
      ref={containerRef}
      className="card director-daily-brief"
      aria-label="Director's daily brief"
      data-feedback-id="director-daily-brief"
      data-entity-type="ai_run"
      data-entity-id={brief?.runId || ''}
    >
      <header className="ddb-head">
        <div className="ddb-head-titles">
          <div className="eyebrow">{"Director's daily brief"}</div>
          <AiGroundingChip
            groundedIn={
              brief?.groundedIn || {
                route: 'executive',
                routeLabel: 'Executive Command Center',
              }
            }
          />
        </div>
        <div className="row ddb-head-meta">
          {asOfLabel ? (
            <span className="chip slate sm" data-testid="ddb-as-of">
              {asOfLabel}
            </span>
          ) : null}
          {hasBrief ? (
            <StatusChip status={brief.reviewState || 'pending_human_review'}>
              {acknowledged ? 'Human reviewed' : 'Pending human review'}
            </StatusChip>
          ) : null}
          {hasBrief ? <ProvenanceBadge value={brief} kind="ai" /> : null}
        </div>
      </header>

      {refreshMut.error ? <ErrorState error={refreshMut.error} onRetry={refreshBrief} /> : null}

      {refreshMut.running || initialLoading ? (
        <div className="ddb-skeleton" aria-live="polite">
          <div className="skeleton" style={{ height: 14, width: '92%' }} />
          <div className="skeleton" style={{ height: 14, width: '85%' }} />
          <div className="skeleton" style={{ height: 14, width: '70%' }} />
          <p className="muted small">{BUSY_LABEL}</p>
        </div>
      ) : hasBrief ? (
        <>
          <div className="ddb-narrative">
            {paragraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
          {provenance?.confidence.value != null ? (
            <p className="muted small">
              {provenance.confidence.source === 'model'
                ? 'Model confidence'
                : provenance.confidence.source === 'rules'
                  ? 'Rules confidence'
                  : provenance.confidence.source === 'heuristic'
                    ? 'Heuristic confidence'
                    : provenance.confidence.source === 'authored'
                      ? 'Authored confidence'
                      : 'Confidence · source unknown'}{' '}
              · {Math.round(provenance.confidence.value * 100)}%
            </p>
          ) : null}
          <GovernedRunChrome result={{ ...brief, confidence: null }} />
          {brief.citations?.length ? (
            <div className="ddb-citations">
              <div className="eyebrow">Evidence</div>
              <ul>
                {brief.citations.map((citation, index) => (
                  <li key={citation.citationId || `${citation.source}-${index}`}>
                    <span className="chip outline sm">{citation.source}</span>{' '}
                    <span className="muted small">{citation.excerpt}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : (
        <div className="ddb-empty">
          <p className="muted">
            No brief yet today — generate one from the portfolio facts available to PRIME.
          </p>
        </div>
      )}

      <div className="row ddb-actions">
        <button
          className="btn primary tactile-press"
          onClick={refreshBrief}
          disabled={refreshMut.running}
        >
          {refreshMut.running ? BUSY_LABEL : hasBrief ? 'Refresh brief' : "Generate today's brief"}
        </button>
        {hasBrief ? (
          <button
            className="btn secondary"
            onClick={acknowledgeBrief}
            disabled={reviewMut.running || acknowledged}
          >
            {acknowledged ? 'Acknowledged' : 'Acknowledge'}
          </button>
        ) : null}
        <button className="btn ghost" onClick={dismissBrief}>
          Dismiss
        </button>
      </div>
    </section>
  );
});
